<template>
  <v-card elevation="0">
    <warehouse-entry-grocery-dialog
      v-model="entryDialog"
      :warehouse="editedItem?.warehouse || {}"
      :editedItem="editedItem"
      v-if="editedItem"
      @success="onEntrySuccess"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <!-- Show grouped by warehouse if multiple warehouses selected -->
        <template v-if="filters.warehouse.length > 1">
          <mobile-data-cards
            v-for="(group, warehouseId) in documentsByWarehouse"
            :key="warehouseId"
            :headers="mobileHeaders"
            :items="group.items"
            :loading="loading"
            :title="group.warehouseName"
            item-key="id"
            :fields-per-row="5"
            :show-row-number="false"
            :bold-fields="['number']"
            class="mobile-table-compact"
          >
            <template v-slot:item.document_date="{ item }">
              {{
                localeDate(new Date(item.document_date), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              }}
            </template>
            <template v-slot:item.total_items="{ item }">
              {{ item.groceries ? item.groceries.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-spacer />
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
              />
            </template>
          </mobile-data-cards>
        </template>
        <!-- Show single list if only one warehouse selected -->
        <template v-else>
          <mobile-data-cards
            :headers="mobileHeaders"
            :items="filteredDocuments"
            :loading="loading"
            item-key="id"
            :fields-per-row="5"
            :show-row-number="false"
            :bold-fields="['number']"
            class="mobile-table-compact"
          >
            <template v-slot:item.document_date="{ item }">
              {{
                localeDate(new Date(item.document_date), {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                })
              }}
            </template>
            <template v-slot:item.total_items="{ item }">
              {{ item.groceries ? item.groceries.length : 0 }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-spacer />
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
              />
            </template>
          </mobile-data-cards>
        </template>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-package-variant-plus"
        :title="$t('warehouse.no_entries')"
        :subtitle="$t('warehouse.no_entries_yet')"
      >
        <v-btn color="primary" @click="openAddDialog">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("add_warehouse") }}
        </v-btn>
      </empty-state>

      <!-- Floating Action Button for Mobile -->
      <v-btn
        v-if="filteredDocuments.length > 0"
        color="primary"
        fab
        fixed
        bottom
        right
        @click="openAddDialog"
        class="mb-12"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate View -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="activeAggregatedItems.length > 0 || loading"
          :headers="aggregateHeaders"
          :items="activeAggregatedItems"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
                {{ $t("warehouse.last_entry_date") }}:
                <strong>{{ lastEntryDate }}</strong>
              </span>
              <v-spacer />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
              <warehouse-button-import-articles @click="openAddDialog" />
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) || "-" }}
          </template>
          <template v-slot:item.total_quantity="{ item }">
            {{ formatNumber(item.total_quantity) }}
          </template>
          <template v-slot:item.total_amount="{ item }">
            {{ formatNumber(item.total_amount) || "" }}
          </template>
          <template v-slot:item.avg_unit_price="{ item }">
            {{
              formatNumber(
                item.total_quantity ? item.total_amount / item.total_quantity : 0
              ) || ""
            }}
          </template>
          <template v-slot:item.document_count="{ item }">
            {{ item.document_count }}
          </template>
        </v-data-table>

        <empty-state
          v-if="!loading && activeAggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
          :subtitle="$t('warehouse.no_entries_for_period')"
        />
      </template>

      <!-- Regular Document View -->
      <v-data-table
        v-else-if="displayedDocuments.length > 0 || loading"
        :headers="headers"
        :items="displayedDocuments"
        :loading="loading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_entry_date") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
            <v-spacer />
            <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
              <v-btn :value="true" small>
                <v-icon left small>mdi-sigma</v-icon>
                {{ $t("warehouse.aggregate") }}
              </v-btn>
            </v-btn-toggle>
            <warehouse-button-import-articles @click="openAddDialog" />
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.number="{ item }">
          {{ item.number || "-" }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{
            localeDate(new Date(item.document_date), {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.total_items="{ item }">
          {{ item.groceries ? item.groceries.length : 0 }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            @click="openDocument(item)"
            icon="mdi-eye"
            :tooltip="$t('overview')"
            color="primary"
          />
        </template>
      </v-data-table>

      <empty-state
        v-if="!aggregateMode && !loading && displayedDocuments.length === 0"
        icon="mdi-package-variant-plus"
        :title="
          documents.length
            ? $t('warehouse.no_entries_for_period')
            : $t('warehouse.no_entries')
        "
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_entry_date') + ': ' + lastEntryDate
            : $t('warehouse.no_entries_yet')
        "
      >
        <warehouse-button-import-articles @click="openAddDialog" />
      </empty-state>
    </template>
  </v-card>
</template>

<script>
import WarehouseEntryGroceryDialog from "@/components/warehouse/dialogs/WarehouseEntryGroceryDialog.vue";
import { WarehouseButtonImportArticles } from "@/components/warehouse/buttons";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import { documentGrocery } from "@/models/warehouse";

export default {
  mixins: [warehouseDocumentTabMixin],
  components: {
    WarehouseEntryGroceryDialog,
    WarehouseButtonImportArticles,
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      documentType: "entry",
      entryDialog: false,
      editedItem: {},
      aggregateMode: false,
    };
  },
  methods: {
    onEntrySuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    openDocument(item) {
      this.editedItem = { ...item };
      this.entryDialog = true;
    },
    openAddDialog() {
      this.editedItem = {
        ...documentGrocery,
        warehouse: this.filters.warehouse[0] || {},
      };
      this.entryDialog = true;
    },
    formatNumber(val) {
      if (!val) return "";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  computed: {
    displayedDocuments() {
      if (!this.filters.groceryCode) return this.filteredDocuments;
      return this.filteredDocuments.filter((doc) =>
        (doc.groceries || []).some((g) => g.code === this.filters.groceryCode.code)
      );
    },
    activeAggregatedItems() {
      return this.filters.groceryCode ? this.aggregatedItems : this.allAggregatedItems;
    },
    aggregatedItems() {
      if (!this.filters.groceryCode) return [];
      const result = { total_quantity: 0, total_amount: 0, document_count: 0 };
      this.filteredDocuments.forEach((doc) => {
        (doc.groceries || []).forEach((g) => {
          if (g.code === this.filters.groceryCode.code) {
            result.total_quantity += Number(g.quantity) || Number(g.amount) || 0;
            result.total_amount +=
              (Number(g.amount) || Number(g.quantity) || 0) * (Number(g.unit_price) || 0);
            result.document_count++;
          }
        });
      });
      if (result.document_count === 0) return [];
      result.grocery_name = this.filters.groceryCode.name;
      result.grocery_code = this.filters.groceryCode.code;
      result.measure = this.filters.groceryCode.measure;
      result.avg_unit_price =
        result.total_quantity > 0 ? result.total_amount / result.total_quantity : 0;
      return [result];
    },
    allAggregatedItems() {
      const groceryMap = {};
      this.filteredDocuments.forEach((doc) => {
        (doc.groceries || []).forEach((g) => {
          if (!g.id) return;
          if (!groceryMap[g.id]) {
            groceryMap[g.id] = {
              grocery_name: g.name,
              grocery_code: g.code,
              measure: g.measure,
              total_quantity: 0,
              total_amount: 0,
              document_count: 0,
            };
          }
          const entry = groceryMap[g.id];
          entry.total_quantity += Number(g.quantity) || Number(g.amount) || 0;
          entry.total_amount +=
            (Number(g.amount) || Number(g.quantity) || 0) * (Number(g.unit_price) || 0);
          entry.document_count++;
        });
      });
      return Object.values(groceryMap)
        .map((item) => ({
          ...item,
          avg_unit_price:
            item.total_quantity > 0 ? item.total_amount / item.total_quantity : 0,
        }))
        .sort((a, b) => (a.grocery_name || "").localeCompare(b.grocery_name || ""));
    },
    aggregateHeaders() {
      return [
        { text: this.$t("code"), value: "grocery_code", width: "100px" },
        { text: this.$t("warehouse.product"), value: "grocery_name", width: "350px" },
        {
          text: this.$t("unit_of_measurment"),
          value: "measure",
          width: "90px",
          align: "center",
        },
        {
          text: this.$t("warehouse.total_quantity"),
          value: "total_quantity",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.avg_unit_price"),
          value: "avg_unit_price",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.total_amount"),
          value: "total_amount",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.document_count"),
          value: "document_count",
          align: "center",
          width: "80px",
        },
      ];
    },
    filteredDocuments() {
      const { from, to } = this.filters.dates;
      if (!from || !to) {
        return this.documents.filter((item) =>
          this.filters.warehouse.some((warehouse) => warehouse.id === item.warehouse_id)
        );
      }

      const fromDate = new Date(from + "T00:00:00");
      const toDate = new Date(to + "T23:59:59");

      return this.documents.filter((item) => {
        const documentDate = new Date(item.document_date);
        const dateFilter = documentDate >= fromDate && documentDate <= toDate;
        const warehouseFilter = this.filters.warehouse.some(
          (warehouse) => warehouse.id === item.warehouse_id
        );
        return dateFilter && warehouseFilter;
      });
    },
    headers() {
      return [
        {
          text: "ID",
          value: "id",
          cols: 3,
          mobile: false,
        },
        {
          text: this.$t("stock"),
          align: "start",
          value: "warehouse.name",
          cols: 4,
          mobile: false,
        },
        {
          text: this.$t("supplier"),
          value: "supplier.name",
          mobile: false,
        },
        {
          text: this.$t("warehouse.document_number"),
          value: "number",
          cols: 3,
        },
        {
          text: this.$t("date"),
          value: "document_date",
          cols: 4,
          print: false,
        },
        {
          text: this.$t("warehouse.items_count"),
          value: "total_items",
          cols: 3,
          align: "center",
        },
        { text: this.$t("status_created"), value: "created_at", mobile: false },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          print: false,
          cols: 2,
          width: "100px",
        },
      ];
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("warehouse.unknown"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    exportHeaders() {
      if (this.aggregateMode) return this.aggregateHeaders;
      return this.headers.filter((h) => h.value !== "actions");
    },
    exportItems() {
      if (this.aggregateMode) {
        return this.activeAggregatedItems.map((item) => ({
          ...item,
          total_quantity: this.formatNumber(item.total_quantity),
          avg_unit_price: this.formatNumber(item.avg_unit_price),
          total_amount: this.formatNumber(item.total_amount),
        }));
      }
      return this.displayedDocuments.map((item) => ({
        ...item,
        total_items: item.groceries ? item.groceries.length : 0,
      }));
    },
    exportTitle() {
      const warehouse =
        this.filters.warehouse.map((w) => w.name).join(", ") || this.$t("stock");
      const type = this.$t("warehouse.warehouse_replenishment");
      if (this.aggregateMode)
        return [warehouse, type, this.$t("warehouse.aggregate")].join(" ");
      return [warehouse, type].join(" ");
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
      immediate: true,
    },
    documents: {
      handler(docs) {
        const groceryMap = {};
        (docs || []).forEach((doc) => {
          (doc.groceries || []).forEach((g) => {
            if (g.id && !groceryMap[g.id]) {
              groceryMap[g.id] = {
                id: g.id,
                code: g.code,
                name: g.name,
                measure: g.measure,
              };
            }
          });
        });
        this.$store.commit(
          "warehouse/SET_CODE_FILTER_GROCERIES",
          Object.values(groceryMap)
        );
      },
      immediate: true,
    },
    isExcelDownloading(val) {
      if (val) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.exportHeaders,
              items: this.exportItems,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting(val) {
      if (val) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [{ headers: this.exportHeaders, items: this.exportItems }],
        });
      }
    },
  },
};
</script>
